import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, X } from "lucide-react";
import {
	CvTemplate,
	CreateCvTemplateData,
	CvSectionBlock,
	CustomColors,
	CvLayout,
	DEFAULT_SECTION_BLOCKS,
	isLegacySections,
} from "@/types/cvTemplate";
import { useCvTemplates } from "@/hooks/useCvTemplates";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/providers/AuthProvider";
import { CvBuilderPanel } from "./cv-builder/CvBuilderPanel";
import { CvPreviewPanel } from "./cv-builder/CvPreviewPanel";
import { generateHtmlFromSections } from "@/services/cvGenerationService";

interface CvTemplateModalProps {
	isOpen: boolean;
	onClose: () => void;
	template?: CvTemplate | null;
}

const EMPTY_CANDIDATE = { id: "", name: "", email: "" };

const DEFAULT_CUSTOM_COLORS: CustomColors = {
	primary: "#2563eb",
	secondary: "#64748b",
	background: "#f8fafc",
	text: "#1e293b",
};

export const CvTemplateModal = ({ isOpen, onClose, template }: CvTemplateModalProps) => {
	const { t } = useTranslation();
	const { toast } = useToast();
	const { createTemplate, updateTemplate } = useCvTemplates();
	const { currentOrganization } = useAuth();

	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [sections, setSections] = useState<CvSectionBlock[]>([...DEFAULT_SECTION_BLOCKS]);
	const [colorScheme, setColorScheme] = useState("classic");
	const [customColors, setCustomColors] = useState<CustomColors>({ ...DEFAULT_CUSTOM_COLORS });
	const [layout, setLayout] = useState<CvLayout>("standard");
	const [isSubmitting, setIsSubmitting] = useState(false);

	useEffect(() => {
		if (!isOpen) return;

		if (template) {
			setName(template.name);
			setDescription(template.description || "");
			setColorScheme(template.color_scheme || "classic");
			setCustomColors(template.custom_colors || { ...DEFAULT_CUSTOM_COLORS });
			setLayout(template.layout || "standard");

			if (isLegacySections(template.sections))
				setSections([...DEFAULT_SECTION_BLOCKS]);
			else
				setSections(template.sections as CvSectionBlock[]);
		} else {
			setName("");
			setDescription("");
			setSections([...DEFAULT_SECTION_BLOCKS]);
			setColorScheme("classic");
			setCustomColors({ ...DEFAULT_CUSTOM_COLORS });
			setLayout("standard");
		}
	}, [isOpen, template]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (isSubmitting) return;

		if (!name.trim()) {
			toast({
				title: t("admin.cvTemplates.validationError", "Validation Error"),
				description: t("admin.cvTemplates.nameRequired", "Please fill in the template name."),
				variant: "destructive",
			});
			return;
		}

		setIsSubmitting(true);
		try {
			const content = generateHtmlFromSections({
				sections,
				candidate: EMPTY_CANDIDATE as any,
				experiences: [],
				skills: [],
				organizationName: currentOrganization?.name,
				colorScheme,
				customColors: colorScheme === "custom" ? customColors : undefined,
				layout,
			});

			const data: CreateCvTemplateData = {
				name: name.trim(),
				description: description.trim() || undefined,
				content,
				sections,
				color_scheme: colorScheme,
				custom_colors: colorScheme === "custom" ? customColors : undefined,
				layout,
			};

			if (template)
				await updateTemplate(template.id, data);
			else
				await createTemplate(data);

			onClose();
		} catch {
			// Error handled in hook
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className="w-[95vw] max-w-[1600px] h-[90vh] p-0 flex flex-col" aria-describedby="cv-template-description">
				<DialogHeader className="px-6 py-3 border-b flex-shrink-0">
					<DialogTitle>
						{template
							? t("admin.cvTemplates.editTemplate", "Edit CV Template")
							: t("admin.cvTemplates.createTemplate", "Create CV Template")
						}
					</DialogTitle>
					<p id="cv-template-description" className="sr-only">
						Configure CV template sections, colors, and layout.
					</p>
				</DialogHeader>

				<form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
					{/* Side-by-side: Build + Preview */}
					<div className="flex flex-1 overflow-hidden">
						{/* Left: Builder */}
						<div className="w-1/2 flex flex-col overflow-hidden border-r">
							{/* Name + Description */}
							<div className="px-6 py-4 border-b flex-shrink-0">
								<div className="grid grid-cols-2 gap-3">
									<div className="space-y-1">
										<Label htmlFor="name" className="text-xs">{t("admin.cvTemplates.name", "Template Name")} *</Label>
										<Input
											id="name"
											value={name}
											onChange={(e) => setName(e.target.value)}
											placeholder={t("admin.cvTemplates.namePlaceholder", "Enter template name")}
											disabled={isSubmitting}
											className="h-8"
										/>
									</div>
									<div className="space-y-1">
										<Label htmlFor="description" className="text-xs">{t("admin.cvTemplates.descriptionLabel", "Description")}</Label>
										<Input
											id="description"
											value={description}
											onChange={(e) => setDescription(e.target.value)}
											placeholder={t("admin.cvTemplates.descriptionPlaceholder", "Optional description")}
											disabled={isSubmitting}
											className="h-8"
										/>
									</div>
								</div>
							</div>

							{/* Builder panel */}
							<div className="flex-1 overflow-auto px-6 py-4">
								<CvBuilderPanel
									sections={sections}
									onSectionsChange={setSections}
									colorScheme={colorScheme}
									onColorSchemeChange={setColorScheme}
									customColors={customColors}
									onCustomColorsChange={setCustomColors}
									layout={layout}
									onLayoutChange={setLayout}
									disabled={isSubmitting}
								/>
							</div>
						</div>

						{/* Right: Preview */}
						<div className="w-1/2 overflow-hidden flex flex-col">
							<div className="px-6 py-2 border-b flex-shrink-0">
								<span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
									{t("admin.cvBuilder.previewTab", "Preview")}
								</span>
							</div>
							<div className="flex-1 overflow-auto p-4">
								<CvPreviewPanel
									sections={sections}
									colorScheme={colorScheme}
									customColors={colorScheme === "custom" ? customColors : undefined}
									layout={layout}
									organizationName={currentOrganization?.name}
								/>
							</div>
						</div>
					</div>

					{/* Actions */}
					<div className="px-6 py-3 border-t flex justify-end gap-2 flex-shrink-0">
						<Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
							<X className="h-4 w-4 mr-1" />
							{t("admin.cvTemplates.cancel", "Cancel")}
						</Button>
						<Button type="submit" disabled={isSubmitting} className="bg-digger-blue hover:bg-digger-blue-light">
							<Save className="h-4 w-4 mr-1" />
							{isSubmitting
								? t("admin.cvTemplates.saving", "Saving...")
								: (template
									? t("admin.cvTemplates.update", "Update Template")
									: t("admin.cvTemplates.create", "Create Template")
								)
							}
						</Button>
					</div>
				</form>
			</DialogContent>
		</Dialog>
	);
};
